import { useCallback, useEffect, useRef, useState } from 'react';

export type AudioPref = 'text' | 'audio' | 'both';

/**
 * Defaults to 'both' as soon as audio is known to be available for this
 * chapter — the learner doesn't have to opt in. A manual change (via the
 * selector) always wins over that auto-default for the rest of the session,
 * even if `hasAudio` re-fires.
 */
export function useAudioPref(hasAudio: boolean): [AudioPref, (p: AudioPref) => void] {
  const [pref, setPrefState] = useState<AudioPref>('text');
  const userChangedRef = useRef(false);

  useEffect(() => {
    if (hasAudio && !userChangedRef.current) setPrefState('both');
  }, [hasAudio]);

  const setPref = useCallback((p: AudioPref) => {
    userChangedRef.current = true;
    setPrefState(p);
  }, []);

  return [pref, setPref];
}

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function authHeader(): HeadersInit {
  const token = JSON.parse(localStorage.getItem('simultrain-player-auth') ?? '{}')?.state?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Plays cached TTS audio for a piece of text, matched by SHA-256 of the
 * exact text — the same hash the Studio computed when generating it, so no
 * separate lookup endpoint is needed. `speak()` resolves once playback ends
 * (so callers — e.g. the instruction-mode auto-advance loop — can wait for
 * narration to actually finish instead of racing a fixed timer), or resolves
 * immediately if the learner hasn't opted into audio, no clip exists yet for
 * this text, or autoplay is blocked. Text is always shown regardless.
 */
export function useSpokenAudio(
  ttsAudioMap: Record<string, string>,
  pref: AudioPref,
  assetUrl: (id: string) => string,
) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastObjectUrlRef = useRef<string | null>(null);

  const speak = useCallback(
    (text: string): Promise<void> =>
      new Promise((resolve) => {
        void (async () => {
          if (pref === 'text' || !text.trim()) return resolve();
          const hash = await sha256Hex(text);
          const assetId = ttsAudioMap[hash];
          const el = audioRef.current;
          if (!assetId || !el) return resolve();
          try {
            const res = await fetch(assetUrl(assetId), { headers: authHeader() });
            if (!res.ok) return resolve();
            const blob = await res.blob();
            if (lastObjectUrlRef.current) URL.revokeObjectURL(lastObjectUrlRef.current);
            const url = URL.createObjectURL(blob);
            lastObjectUrlRef.current = url;
            el.src = url;
            const onSettled = () => {
              el.removeEventListener('ended', onSettled);
              el.removeEventListener('error', onSettled);
              resolve();
            };
            el.addEventListener('ended', onSettled);
            el.addEventListener('error', onSettled);
            // Autoplay can be blocked before the learner has interacted with
            // the page at all — don't hang forever if so.
            await el.play().catch(onSettled);
          } catch {
            resolve();
          }
        })();
      }),
    [pref, ttsAudioMap, assetUrl],
  );

  const replay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !lastObjectUrlRef.current) return;
    el.currentTime = 0;
    void el.play().catch(() => {});
  }, []);

  useEffect(
    () => () => {
      if (lastObjectUrlRef.current) URL.revokeObjectURL(lastObjectUrlRef.current);
    },
    [],
  );

  return { audioRef, speak, replay };
}
